import React from "react";
import formStyles from "@/components/DynamicForm/index.module.less";
import { Button } from "antd";

// ==================== 类型定义 ====================

/**
 * 第二步表单数据结构
 */
export interface SupplierStep2FormData {
  // 供应商设置
  factoryName?: string;
  factoryValue?: string;
  materialCategoryName?: string;
  materialCategoryValue?: string;
  materialTwoCategoryName?: string;
  materialTwoCategoryValue?: string;
  materialPurposeName?: string;
  materialPurposeValue?: string;
  isGmpName?: string;
  isGmpValue?: string;
  purchaseTypeName?: string;
  purchaseTypeValue?: string;
  supplierLevelName?: string;
  supplierLevelValue?: string;

  // 采购信息
  purchaseGroupName?: string;
  purchaseGroupValue?: string;
  currencyName?: string;
  currencyValue?: string;
  paymentMethodName?: string;
  paymentMethodValue?: string;
  paymentTermsName?: string;
  paymentTermsValue?: string;
  internationalTradeTermsName?: string;
  internationalTradeTermsValue?: string;
  shoppingName?: string;

  // 财务信息
  accountGroupName?: string;
  accountGroupValue?: string;
  accountCode?: string;

  // 其他合作信息
  oaAddress?: string;
  oaAddressCode?: string;
  piInvoiceParty?: string;
  piInvoicePartyCode?: string;
}

// ==================== 常量配置 ====================

/**
 * 是否为GMP的选项值
 */
const IS_GMP_OPTION_VALUES = ["Y", "N"] as const;

/**
 * 物料类别选项值
 */
const COMMODITY_TYPE_OPTION_VALUES = ["PO", "nonPO"] as const;

/**
 * 二级物料类别选项值 - 生产物料
 */
const MATERIAL_TWO_CATEGORY_PO_OPTION_VALUES = [
  "PO_1",
  "PO_2",
  "PO_3",
  "PO_4",
] as const;

/**
 * 二级物料类别选项值 - 非生产物料
 */
const MATERIAL_TWO_CATEGORY_NON_PO_OPTION_VALUES = [
  "nonPO_1",
  "nonPO_2",
  "nonPO_3",
  "nonPO_4",
  "nonPO_5",
  "nonPO_6",
] as const;

/**
 * 物料目的选项值
 */
const MATERIAL_PURPOSE_OPTION_VALUES = ["P", "nonP", "T", "L"] as const;

/**
 * 采购类型选项值
 */
const PURCHASE_TYPE_OPTION_VALUES = ["A", "B", "O"] as const;

/**
 * 供应商等级选项值
 */
const SUPPLIER_LEVEL_OPTION_VALUES = ["GV", "IV", "SV"] as const;

/**
 * 采购小组选项值
 */
const PURCHASE_GROUP_OPTION_VALUES = [
  "C01",
  "C02",
  "C03",
  "C04",
  "C05",
  "C06",
  "C07",
] as const;

/**
 * 货币选项值
 */
const CURRENCY_OPTION_VALUES = [
  "HKD",
  "CNY",
  "EUR",
  "USD",
  "GBP",
  "TWD",
  "AUD",
  "JPY",
  "VND",
  "CHF",
] as const;

/**
 * 付款方式选项值
 */
const PAYMENT_METHOD_OPTION_VALUES = ["TT", "CHE", "OTH"] as const;

/**
 * 付款条约选项值
 */
const PAYMENT_TERMS_OPTION_VALUES = [
  "ADV",
  "COD",
  "DADV",
  "DEPO",
  "L120",
  "L15",
  "L30",
  "L45",
  "L60",
  "L75",
  "L90",
  "LC",
  "M030",
  "M035",
  "M045",
  "Z007",
  "Z014",
  "Z015",
  "Z030",
  "Z035",
  "Z040",
  "Z045",
  "Z060",
  "Z075",
  "Z085",
  "Z090",
  "Z120",
  "ZCAD",
] as const;

/**
 * 国际贸易条款选项值（Incoterms）
 */
const INCOTERMS_OPTION_VALUES = [
  "CFR",
  "CIF",
  "CIP",
  "CPT",
  "DAF",
  "DDP",
  "DDU",
  "DEQ",
  "DES",
  "EXW",
  "FAS",
  "FCA",
  "FH",
  "FOB",
  "UN",
] as const;

/**
 * 账户小组选项值
 */
const ACCOUNT_GROUP_OPTION_VALUES = [
  "PG01",
  "PG02",
  "PG03",
  "PG05",
  "PG06",
  "PG07",
] as const;

/**
 * 工厂选项值
 */
const FACTORY_OPTION_VALUES = ["1000", "2000", "4000", "3000"] as const;

/**
 * 选项标签映射表
 */
const OPTION_LABELS: Record<string, string> = {
  // GMP选项
  Y: "Yes",
  N: "No",
  // 物料类别
  PO: "生产物料",
  nonPO: "非生产物料",
  // 二级物料类别 - 生产物料
  PO_1: "API",
  PO_2: "辅料（Excipient）",
  PO_3: "包材",
  PO_4: "RLD",
  // 二级物料类别 - 非生产物料
  nonPO_1: "工程建设",
  nonPO_2: "设备/设施/仪器",
  nonPO_3: "MRO",
  nonPO_4: "行政物资",
  nonPO_5: "IT物资",
  nonPO_6: "外包服务",
  // 物料目的
  P: "生产物料",
  nonP: "非生产物料",
  T: "中试物料",
  L: "小试物料",
  // 采购类型
  A: "A类物料（战略物料）",
  B: "B、C类物料+AF委外采购产品",
  O: "OEM客户专用物料",
  // 供应商等级
  GV: "一般供应商",
  IV: "重点供应商",
  SV: "战略供应商",
  // 采购小组
  C01: "C01 Dir.Matl-Plant 工厂-生产物料",
  C02: "C02 Indir.Matl-MRO 工厂-易耗品",
  C03: "C03 Indir.Matl-Other 工厂-固定资产/项目",
  C04: "C04 Dir.Matl-Other 研发-小试物料",
  C05: "C05 Dir.Matl-For E5 工厂-生产物料",
  C06: "C06 Idir.Matl-R&D 研发-固定资产/项目",
  C07: "C07 Dir.Matl-Scale-up 研发-中试物料",
  // 货币
  HKD: "HKD",
  CNY: "CNY",
  EUR: "EUR",
  USD: "USD",
  GBP: "GBP",
  TWD: "TWD",
  AUD: "AUD",
  JPY: "JPY",
  VND: "VND",
  CHF: "CHF",
  // 付款方式
  TT: "TT",
  CHE: "Cheque",
  OTH: "Other",
  // 付款条约
  ADV: "Advance payment",
  COD: "Cash on Delivery",
  DADV: "Advance payment by progress, refer to remark",
  DEPO: "Deposit and progress payment, refer to remark",
  L120: "L/C 120 days",
  L15: "L/C 15 days",
  L30: "L/C 30 days",
  L45: "L/C 45 days",
  L60: "L/C 60 days",
  L75: "L/C 75 days",
  L90: "L/C 90 days",
  LC: "L/C at sight",
  M030: "30 days from month end",
  M035: "35 days from month end",
  M045: "45 days from month end",
  Z007: "Net due on 7 days",
  Z014: "Net due on 14 days",
  Z015: "Net due on 15 days",
  Z030: "Net due on 30 days",
  Z035: "Net due on 35 days",
  Z040: "Net due on 40 days",
  Z045: "Net due on 45 days",
  Z060: "Net due on 60 days",
  Z075: "Net due on 75 days",
  Z085: "Net due on 85 days",
  Z090: "Net due on 90 days",
  Z120: "Net due on 120 days",
  ZCAD: "Cash against documents",
  // 国际贸易条款
  CFR: "Costs and freight",
  CIF: "Costs, insurance & freight",
  CIP: "Carriage and insurance paid to",
  CPT: "Carriage paid to",
  DAF: "Delivered at frontier",
  DDP: "Delivered Duty Paid",
  DDU: "Delivered Duty Unpaid",
  DEQ: "Delivered ex quay (duty paid)",
  DES: "Delivered ex ship",
  EXW: "Ex Works",
  FAS: "Free Alongside Ship",
  FCA: "Free Carrier",
  FH: "Free house",
  FOB: "Free on board",
  UN: "Not Free",
  // 账户小组
  PG01: "BF-Production Material Vendor",
  PG02: "BF-Production Material Agent",
  PG03: "BF-Non-production Vendor",
  PG05: "BF-Vendor (FI Only)",
  PG06: "BF-Intercompany Vendor",
  PG07: "BF-Equipment Vendor",
  // 工厂
  "1000": "E1 Bright Future",
  "2000": "E2 BF Chinese Medicine",
  "4000": "E4 SumaUS",
  "3000": "E5 SumaHK",
};

/**
 * 科目小组与科目编号的对应关系映射
 */
const ACCOUNT_NUMBER_MAPPING: Record<string, string[]> = {
  PG01: ["212010"],
  PG02: ["212010"],
  PG03: ["212090"],
  PG04: ["212090"],
  PG05: ["117110"],
  PG06: ["117910"],
  PG07: ["212090"],
  PG09: ["212090"],
};

// ==================== 辅助函数 ====================

/**
 * 根据值从标签映射表中查找对应的标签
 * @param value 选项值
 * @param t 翻译函数
 * @returns 对应的标签，如果未找到则返回原值
 */
const getLabelByValue = (
  value: string | undefined,
  t?: (key: string) => string,
): string | undefined => {
  if (!value) return undefined;
  const translate = t || ((key: string) => key);
  const label = OPTION_LABELS[value];
  return label ? translate(label) : value;
};

/**
 * 生成是否为GMP的选项
 */
const getIsGmpOptions = (t?: (key: string) => string) => {
  const translate = t || ((key: string) => key);
  return IS_GMP_OPTION_VALUES.map((value) => ({
    label: translate(OPTION_LABELS[value]),
    value,
  }));
};

/**
 * 生成物料类别选项
 */
const getCommodityTypeOptions = (t?: (key: string) => string) => {
  const translate = t || ((key: string) => key);
  return COMMODITY_TYPE_OPTION_VALUES.map((value) => ({
    label: translate(OPTION_LABELS[value]),
    value,
  }));
};

/**
 * 生成二级物料类别选项 - 生产物料
 */
const getMaterialTwoCategoryPoOptions = (t?: (key: string) => string) => {
  const translate = t || ((key: string) => key);
  return MATERIAL_TWO_CATEGORY_PO_OPTION_VALUES.map((value) => ({
    label: translate(OPTION_LABELS[value]),
    value,
  }));
};

/**
 * 生成二级物料类别选项 - 非生产物料
 */
const getMaterialTwoCategoryNonPoOptions = (t?: (key: string) => string) => {
  const translate = t || ((key: string) => key);
  return MATERIAL_TWO_CATEGORY_NON_PO_OPTION_VALUES.map((value) => ({
    label: translate(OPTION_LABELS[value]),
    value,
  }));
};

/**
 * 根据物料类别获取对应的二级物料类别选项
 */
const getMaterialTwoCategoryOptions = (
  materialCategoryValue: string,
  t?: (key: string) => string,
) => {
  if (materialCategoryValue === "PO") {
    return getMaterialTwoCategoryPoOptions(t);
  }
  if (materialCategoryValue === "nonPO") {
    return getMaterialTwoCategoryNonPoOptions(t);
  }
  return [];
};

/**
 * 生成物料目的选项
 */
const getMaterialPurposeOptions = (t?: (key: string) => string) => {
  const translate = t || ((key: string) => key);
  return MATERIAL_PURPOSE_OPTION_VALUES.map((value) => ({
    label: translate(OPTION_LABELS[value]),
    value,
  }));
};

/**
 * 生成采购类型选项
 */
const getPurchaseTypeOptions = (t?: (key: string) => string) => {
  const translate = t || ((key: string) => key);
  return PURCHASE_TYPE_OPTION_VALUES.map((value) => ({
    label: translate(OPTION_LABELS[value]),
    value,
  }));
};

/**
 * 生成供应商等级选项
 */
const getSupplierLevelOptions = (t?: (key: string) => string) => {
  const translate = t || ((key: string) => key);
  return SUPPLIER_LEVEL_OPTION_VALUES.map((value) => ({
    label: translate(OPTION_LABELS[value]),
    value,
  }));
};

/**
 * 生成采购小组选项
 */
const getPurchaseGroupOptions = (t?: (key: string) => string) => {
  const translate = t || ((key: string) => key);
  return PURCHASE_GROUP_OPTION_VALUES.map((value) => ({
    label: translate(OPTION_LABELS[value]),
    value,
  }));
};

/**
 * 生成货币选项
 */
const getCurrencyOptions = (t?: (key: string) => string) => {
  const translate = t || ((key: string) => key);
  return CURRENCY_OPTION_VALUES.map((value) => ({
    label: translate(OPTION_LABELS[value]),
    value,
  }));
};

/**
 * 生成付款方式选项
 */
const getPaymentMethodOptions = (t?: (key: string) => string) => {
  const translate = t || ((key: string) => key);
  return PAYMENT_METHOD_OPTION_VALUES.map((value) => ({
    label: translate(OPTION_LABELS[value]),
    value,
  }));
};

/**
 * 生成付款条约选项
 */
const getPaymentTermsOptions = (t?: (key: string) => string) => {
  const translate = t || ((key: string) => key);
  return PAYMENT_TERMS_OPTION_VALUES.map((value) => ({
    label: translate(OPTION_LABELS[value]),
    value,
  }));
};

/**
 * 生成国际贸易条款选项
 */
const getIncotermsOptions = (t?: (key: string) => string) => {
  const translate = t || ((key: string) => key);
  return INCOTERMS_OPTION_VALUES.map((value) => ({
    label: translate(OPTION_LABELS[value]),
    value,
  }));
};

/**
 * 生成账户小组选项
 */
const getAccountGroupOptions = (t?: (key: string) => string) => {
  const translate = t || ((key: string) => key);
  return ACCOUNT_GROUP_OPTION_VALUES.map((value) => ({
    label: translate(OPTION_LABELS[value]),
    value,
  }));
};

/**
 * 生成工厂选项
 */
const getFactoryOptions = (t?: (key: string) => string) => {
  const translate = t || ((key: string) => key);
  return FACTORY_OPTION_VALUES.map((value) => ({
    label: translate(OPTION_LABELS[value]),
    value,
  }));
};

/**
 * 将第二步表单数据中的值转换为名称+值的对应形式
 * 即从 { factoryValue: "1000", ... } 转换为 { factoryValue: "1000", factoryName: "E1 Bright Future", ... }
 */
const transformStep2FormDataWithNames = (
  formData: any,
  t?: (key: string) => string,
) => {
  return {
    ...formData,
    // 工厂
    factoryName: getLabelByValue(formData.factoryValue, t),
    // 物料类别
    materialCategoryName: getLabelByValue(formData.materialCategoryValue, t),
    // 二级物料类别
    materialTwoCategoryName: getLabelByValue(
      formData.materialTwoCategoryValue,
      t,
    ),
    // 物料目的
    materialPurposeName: getLabelByValue(formData.materialPurposeValue, t),
    // 是否为GMP
    isGmpName: getLabelByValue(formData.isGmpValue, t),
    // 采购类型
    purchaseTypeName: getLabelByValue(formData.purchaseTypeValue, t),
    // 供应商等级
    supplierLevelName: getLabelByValue(formData.supplierLevelValue, t),
    // 采购小组
    purchaseGroupName: getLabelByValue(formData.purchaseGroupValue, t),
    // 货币
    currencyName: getLabelByValue(formData.currencyValue, t),
    // 付款方式
    paymentMethodName: getLabelByValue(formData.paymentMethodValue, t),
    // 付款条约
    paymentTermsName: getLabelByValue(formData.paymentTermsValue, t),
    // 国际贸易条款
    internationalTradeTermsName: getLabelByValue(
      formData.internationalTradeTermsValue,
      t,
    ),
    // 科目小组
    accountGroupName: getLabelByValue(formData.accountGroupValue, t),
  };
};

// ==================== 渲染函数 ====================

/**
 * 获取第一步表单底部按钮
 * @param handleSubmitSave 暂存回调
 * @param handleNext 下一步回调
 * @param t 翻译函数
 */
const getStep1ModalFooter = (
  handleSubmitSave: () => void,
  handleNext: () => void,
  t?: (key: string) => string,
) => {
  const translate = t || ((key: string) => key);
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        gap: "12px",
        paddingTop: "20px",
      }}
    >
      <Button type="primary" onClick={handleNext}>
        {translate("下一步")}
      </Button>
    </div>
  );
};

/**
 * 获取第二步表单底部按钮
 *
 * @param handlePrev 上一步回调
 * @param handleSubmitSave 暂存回调
 * @param handleSubmitApprove 提交回调
 * @param showSaveButton 是否显示暂存按钮（在编辑和申请变更模式下不显示）
 * @param isFromWorkbench 是否从工作台进入，为true时只显示提交按钮
 * @param t 翻译函数
 */
const getStep2ModalFooter = (
  handlePrev: () => void,
  handleSubmitSave: () => void,
  handleSubmitApprove: () => void,
  showSaveButton: boolean = true,
  isFromWorkbench: boolean = false,
  t?: any,
) => {
  const translate = t || ((key: string) => key);

  // 从工作台进入时，只显示上一步和提交按钮
  if (isFromWorkbench) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "12px",
          paddingTop: "20px",
        }}
      >
        <Button onClick={handlePrev}>{translate("上一步")}</Button>
        <Button type="primary" onClick={handleSubmitApprove}>
          {translate("提交")}
        </Button>
      </div>
    );
  }

  // 正常模式下显示上一步、暂存（可选）、提交按钮
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        gap: "12px",
        paddingTop: "20px",
      }}
    >
      <Button onClick={handlePrev}>{translate("上一步")}</Button>
      {showSaveButton && (
        <Button onClick={handleSubmitSave}>{translate("暂存")}</Button>
      )}
      <Button type="primary" onClick={handleSubmitApprove}>
        {translate("提交")}
      </Button>
    </div>
  );
};

export {
  getStep1ModalFooter,
  getStep2ModalFooter,
  ACCOUNT_NUMBER_MAPPING,
  transformStep2FormDataWithNames,
  getMaterialTwoCategoryOptions,
  getIsGmpOptions,
  getCommodityTypeOptions,
  getMaterialTwoCategoryPoOptions,
  getMaterialTwoCategoryNonPoOptions,
  getMaterialPurposeOptions,
  getPurchaseTypeOptions,
  getSupplierLevelOptions,
  getPurchaseGroupOptions,
  getCurrencyOptions,
  getPaymentMethodOptions,
  getPaymentTermsOptions,
  getIncotermsOptions,
  getAccountGroupOptions,
  getFactoryOptions,
};

// ==================== 第二步表单配置 ====================

/**
 * 供应商准入申请第二步表单配置
 * 包含：供应商设置、采购信息、财务信息、其他合作信息
 *
 * @param isFromWorkbench 是否从工作台进入，为true时显示审核相关字段
 * @param onViewEvaluation 点击查看评估结果的回调函数
 * @param t 翻译函数
 */
export const getAdmissionApplicationStep2FormItems = (
  isFromWorkbench: boolean = false,
  onViewEvaluation?: () => void,
  t?: any,
) => {
  const translate = t || ((key: string) => key);

  return [
    // ==================== 供应商设置 ====================
    {
      name: "supplierSettingSection",
      col: { span: 24 },
      custom: {
        render: () => (
          <div className={formStyles.sectionHeader}>
            <div className={formStyles.sectionIndicator} />
            <span className={formStyles.sectionTitle}>
              {translate("供应商设置")}
            </span>
          </div>
        ),
      },
    },
    {
      name: "supplierWrapperStart",
      col: { span: 24 },
      wrapperStart: { className: formStyles.wrapperLight },
    },
    {
      label: translate("供应商编码"),
      name: "supplierCode",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      // rules: [{ required: true, message: translate("请输入供应商编码") }],
      widget: {
        widgetType: "Input",
        placeholder: translate("请输入"),
      },
    },
    {
      label: translate("工厂"),
      name: "factoryValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: translate("请选择工厂") }],
      widget: {
        widgetType: "Select",
        placeholder: translate("请选择"),
        options: getFactoryOptions(t),
      },
    },
    {
      label: translate("物料类别"),
      name: "materialCategoryValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: translate("请选择物料类别") }],
      widget: {
        widgetType: "Select",
        placeholder: translate("请选择"),
        options: getCommodityTypeOptions(t),
      },
    },
    {
      label: translate("二级物料类别"),
      name: "materialTwoCategoryValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      dependencies: ["materialCategoryValue"],
      rules: [{ required: true, message: translate("请选择二级物料类别") }],
      widget: {
        widgetType: "Select",
        placeholder: translate("请选择"),
        options: [],
        optionsGetter: (formValues: any) => {
          return getMaterialTwoCategoryOptions(
            formValues.materialCategoryValue,
            t,
          );
        },
      },
    },
    {
      label: translate("物料目的"),
      name: "materialPurposeValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: translate("请选择物料目的") }],
      widget: {
        widgetType: "Select",
        placeholder: translate("请选择"),
        options: getMaterialPurposeOptions(t),
      },
    },
    {
      label: translate("是否为GMP"),
      name: "isGmpValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: translate("请选择是否为GMP") }],
      widget: {
        widgetType: "Select",
        placeholder: translate("请选择"),
        options: getIsGmpOptions(t),
      },
    },
    {
      label: translate("采购类型"),
      name: "purchaseTypeValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: translate("请选择采购类型") }],
      widget: {
        widgetType: "Select",
        placeholder: translate("请选择"),
        options: getPurchaseTypeOptions(t),
      },
    },
    {
      label: translate("供应商等级"),
      name: "supplierLevelValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Select",
        placeholder: translate("请选择"),
        options: getSupplierLevelOptions(t),
      },
    },

    // ==================== 采购信息 ====================
    {
      name: "purchaseSection",
      col: { span: 24 },
      custom: {
        render: () => (
          <div className={formStyles.subSectionTitle}>
            {translate("采购信息")}
          </div>
        ),
      },
    },
    {
      label: translate("采购小组"),
      name: "purchaseGroupValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: translate("请选择采购小组") }],
      widget: {
        widgetType: "Select",
        placeholder: translate("请选择"),
        options: getPurchaseGroupOptions(t),
      },
    },
    {
      label: translate("货币"),
      name: "currencyValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: translate("请选择货币") }],
      widget: {
        widgetType: "Select",
        placeholder: translate("请选择"),
        options: getCurrencyOptions(t),
      },
    },
    {
      label: translate("付款方式"),
      name: "paymentMethodValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: translate("请选择付款方式") }],
      widget: {
        widgetType: "Select",
        placeholder: translate("请选择"),
        options: getPaymentMethodOptions(t),
      },
    },
    {
      label: translate("付款条约"),
      name: "paymentTermsValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: translate("请选择付款条约") }],
      widget: {
        widgetType: "Select",
        placeholder: translate("请选择"),
        options: getPaymentTermsOptions(t),
      },
    },
    {
      label: translate("国际贸易条款"),
      name: "internationalTradeTermsValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: translate("请选择国际贸易条款") }],
      widget: {
        widgetType: "Select",
        placeholder: translate("请选择"),
        options: getIncotermsOptions(t),
      },
    },
    {
      label: translate("E-Shopping"),
      name: "shoppingName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        placeholder: translate("请输入"),
      },
    },

    // ==================== 财务信息 ====================
    {
      name: "financialSection",
      col: { span: 24 },
      custom: {
        render: () => (
          <div className={formStyles.subSectionTitle}>
            {translate("财务信息")}
          </div>
        ),
      },
    },
    {
      label: translate("科目小组"),
      name: "accountGroupValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: translate("请选择科目小组") }],
      widget: {
        widgetType: "Select",
        placeholder: translate("请选择"),
        options: getAccountGroupOptions(t),
      },
    },
    {
      label: translate("科目编号"),
      name: "accountCode",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      dependencies: ["accountGroupValue"],
      rules: [
        {
          required: true,
          message: translate("请选择科目小组以自动填充科目编号"),
        },
      ],
      widget: {
        widgetType: "Input",
        disabled: true,
      },
    },

    // ==================== 其他合作信息 ====================
    {
      name: "cooperationSection",
      col: { span: 24 },
      custom: {
        render: () => (
          <div className={formStyles.subSectionTitle}>
            {translate("其他合作信息")}
          </div>
        ),
      },
    },
    {
      label: translate("OA地址"),
      name: "oaAddress",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      // rules: [{ required: true, message: "请输入OA订单地址" }],
      widget: {
        widgetType: "Input",
        placeholder: translate("请输入"),
      },
    },
    {
      label: translate("PI发票方"),
      name: "piInvoiceParty",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      // rules: [{ required: true, message: "请输入PI开票方" }],
      widget: {
        widgetType: "Input",
        placeholder: translate("请输入"),
      },
    },
    ...(isFromWorkbench
      ? [
          {
            name: "evaluationResult",
            col: { span: 24 },
            custom: {
              render: () => (
                <div className={formStyles.subSectionTitle}>
                  {translate("准入评估结果")}
                  <Button type="link" size="small" onClick={onViewEvaluation}>
                    {translate("查看评估结果")}
                  </Button>
                </div>
              ),
            },
          },
          {
            label: translate("审核意见"),
            name: "reviewOpinion",
            col: { span: 24 },
            labelCol: { span: 3 },
            wrapperCol: { span: 20 },
            className: "inlineLabel",
            rules: [{ required: true, message: translate("请选择审核意见") }],
            widget: {
              widgetType: "Radio.Group",
              options: [
                { label: translate("同意"), value: "agree" },
                { label: translate("驳回"), value: "reject" },
              ],
              disabled: false,
            },
          },
          {
            label: translate("意见说明"),
            name: "reviewComments",
            col: { span: 24 },
            labelCol: { span: 3 },
            wrapperCol: { span: 22 },
            className: "inlineLabel",
            rules: [{ required: true, message: translate("请输入意见说明") }],
            widget: {
              widgetType: "Input.TextArea",
              placeholder: translate("请输入..."),
              rows: 4,
              style: { resize: "none" },
              disabled: false,
            },
          },
        ]
      : []),
    {
      name: "supplierWrapperEnd",
      col: { span: 24 },
      wrapperEnd: true,
    },
  ];
};
